import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setContactPreference } from '../../../features/registrationSlice';
import { RootState } from '../../../store/store';
import RegWorkflowFactory from '../RegWorkflowFactory';
import { RegWorkflowContainer } from '../RegistrationWorkflow.style';
import { getQuery } from '../../../lib/utils/common';
import {
  AddOrUpdateCRRegDetails,
  addOrUpdateCrRegistrationDetails,
} from '../../../services/RegisterUserService';
import { getCookie } from 'lib/utils/enrollmentUtil';
import { RichText, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { CustomMobileInput } from './CustomMobileInput';
import { Controller, useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { updateUCPMJobRecordsForCR, DataOrigin } from '../../../lib/utils/ucpmHelper';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { updateCRQueryParamList } from '../../../features/crQueryParamSlice';
import { showLoader, hideLoader } from '../../../features/loaderSlice';
import UiText from '../../shared/Typography/UiText';
import {
  StConsentMediumCheckbox,
  StConsentMediumFormControlLabel,
  StConsentMediumFormGroup,
  StConsentMediumGridItem,
} from './ContactPreference.style';

const ContactPreference = (props: any): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const { siteSettings }: any = sitecoreContext;
  const storedAnswers = useSelector(
    (state: RootState) => state.registrationDetails.contactPreference
  );
  const source = siteSettings?.source?.fields?.name?.value || 'CR';
  const isSMSDefaultOptIn = () => {
    if (!showIsMobileOption?.value) {
      return true;
    }
    if (defaultOptinAll?.value) {
      return false;
    }
    const show =
      props?.fields?.consentMediumList &&
      props?.fields?.consentMediumList.some((consentMedium: any) => {
        const fieldName = consentMedium?.fields?.key?.value;
        const defaultOptInState = consentMedium?.fields?.defaultOptIn?.value;
        const defaultHideState = consentMedium?.fields?.hideFromUI?.value;
        if (fieldName === 'SMS') {
          if (!!defaultOptInState && !!defaultHideState) {
            return false;
          } else if (!!defaultOptInState) {
            return true;
          }
          return false;
        }
        return false;
      });
    return show;
  };

  const isSMSConsentVisible = () => {
    const show =
      props?.fields?.consentMediumList &&
      props?.fields?.consentMediumList.some((consentMedium: any) => {
        const fieldName = consentMedium?.fields?.key?.value;
        const defaultHideState = consentMedium?.fields?.hideFromUI?.value;
        if (fieldName === 'SMS') {
          return Boolean(defaultHideState) === false;
        }
        return false;
      });
    return show;
  };

  const getFormDataDefaultValue = (existingValues: any) => {
    return {
      ...existingValues,
      ...storedAnswers,
      isMobile: isSMSDefaultOptIn(),
    };
  };

  useEffect(() => {
    if (storedAnswers?.agree) {
      setIsConsentsAccepted(storedAnswers?.agree);
    }
  }, [storedAnswers]);

  const {
    control,
    setError,
    clearErrors,
    formState: { errors, isValid },
    getValues,
    trigger,
    watch,
  } = useForm({
    mode: 'all',
    defaultValues: async () => getFormDataDefaultValue({}),
  });

  const [isConsentsAccepted, setIsConsentsAccepted] = useState(false);
  const router = useRouter();
  const { query } = router;

  const dispatch = useDispatch();
  const formValues = watch();
  const [enableNext, setEnableNext] = useState<boolean>(false);

  useEffect(() => {
    if (
      Object.keys(errors).length > 0 ||
      (props?.fields?.isPhoneNumberRequired?.value &&
        (formValues.contactNumber === '' || formValues.contactNumber === undefined)) ||
      (props?.fields?.isConsentRequired?.value && !formValues.agree)
    ) {
      setEnableNext(false);
    } else if (
      showIsMobileOption?.value &&
      isSMSDefaultOptIn() &&
      formValues.isMobile === false &&
      formValues.SMS === true
    ) {
      setEnableNext(false);
    } else {
      setEnableNext(true);
    }
  }, [formValues]);

  const phoneNumberRequiredErrorMessage = props?.fields?.phoneNumberRequiredErrorMessage;
  const phoneNumberRegex = props?.fields?.phoneNumberRegex;
  const phoneNumberPlaceholder = props?.fields?.phoneNumberPlaceholder;
  const phoneNumberLabel = props?.fields?.phoneNumberLabel;
  const mobileNumberValidationMessageOnSMSConsent =
    props?.fields?.mobileNumberValidationMessageOnSMSConsent;
  const isPhoneNumberRequired = props?.fields?.isPhoneNumberRequired;
  const isConsentRequired = props?.fields?.isConsentRequired;
  const hideConsentCheckbox = props?.fields?.hideConsentCheckbox;
  const enableAtleastOneContactMediumMandatedOutOfVisible =
    props?.fields?.enableAtleastOneContactMediumMandatedOutOfVisible;

  const enableAtleastOneContactMediumErrorMessage =
    props?.fields?.enableAtleastOneContactMediumErrorMessage;
  const defaultOptinAll = props?.fields?.defaultOptinAll;
  const defaultOptIn = props?.fields?.defaultOptIn;
  const consentTitle = props?.fields?.consentTitle;
  const consentSubTitle = props?.fields?.consentSubTitle;
  const consentRequiredErrorMessage = props?.fields?.consentRequiredErrorMessage;
  const consentMediumList = props?.fields?.consentMediumList;
  const enableVerticalView = props?.fields?.enableVerticalView?.value;
  const IsMobileLabel = props?.fields?.IsMobileLabel;
  const isMobileRequired = props?.fields?.isPhoneNumberRequired?.value;
  const phoneNumberRegexErrorMessage = props?.fields?.phoneNumberRegexErrorMessage;
  const showIsMobileOption = props?.fields?.showIsMobileOption;

  const updateUCPMJobRecords = async () => {
    if ((siteSettings as any)?.enableUCPMIntegration?.value === false) {
      return;
    }
    try {
      return await updateUCPMJobRecordsForCR({
        otSponsorId: siteSettings?.otSponsorId?.value || '',
        parentSponsorMDMID: siteSettings?.parentSponsorMDMEntityId?.value || '',
        protocolMDMID: siteSettings?.parentSponsorMDMEntityId?.value || '',
        sponsor: siteSettings?.sponsorName?.value || '',
        iqviaSponsorId: siteSettings?.iqviaSponsorId?.value || '',
        dataOrigin: DataOrigin.CR_REGISTRATION,
        UCPMStudyName: siteSettings?.studyName?.value || '',
        baseUrl: siteSettings?.baseUrl?.value || '',
      });
    } catch (_e) {}
  };

  const registrationCRDetails = async () => {
    try {
      const guestId = getCookie('bx_guest_ref') || '';
      dispatch(showLoader());
      const transactionalFromNumber = siteSettings?.transactionalFromNumber?.value;
      const isSendSmsEnabled = siteSettings?.enableSmsSendFeature?.value;
      if (process.env.NEXT_PUBLIC_ALLOW_REGISTRATION_API_CALL === 'true') {
        AddOrUpdateCRRegDetails(source, guestId);
      }
      const response = await addOrUpdateCrRegistrationDetails(
        source,
        guestId,
        transactionalFromNumber,
        isSendSmsEnabled
      );
      if (response?.data?.myProfileVerificationToken) {
        sessionStorage.setItem(
          'myProfileVerificationToken',
          response?.data?.myProfileVerificationToken
        );
      }
      dispatch(updateCRQueryParamList({}));
      updateUCPMJobRecords();
      dispatch(hideLoader());
      return response?.data;
    } catch (_e) {
      dispatch(hideLoader());
      return false;
    }
  };

  const renderError = (name: string) => (
    <ErrorMessage
      errors={errors}
      name={name}
      render={({ message }) => (
        <Box
          sx={{
            color: (theme: any) => theme.palette.error.main,
            width: '130px',
            fontSize: '14px',
            marginTop: '1rem',
          }}
        >
          {message}
        </Box>
      )}
    />
  );

  const handleClick = async (event: any, url: any, action: any) => {
    event.preventDefault();
    const formData = getValues();
    dispatch(setContactPreference(formData));

    if (action === 'back') {
      if (url != 'http://') {
        router.push(
          {
            pathname: url,
            query: getQuery(query),
          },
          url
        );
      } else {
        history.back();
      }
      return false;
    }

    trigger();
    if (!isValid) {
      return false;
    }
    const res = await registrationCRDetails();
    if (res !== false) {
      router.push({
        pathname: url,
        query: getQuery(query),
      });
    }
    return false;
  };
  const shouldHideTheBox = () => {
    if (defaultOptinAll?.value) {
      return true;
    }
    return consentMediumList?.every(
      (consentMedium: any) => consentMedium?.fields?.hideFromUI?.value === true
    );
  };

  if (!props?.fields) return <></>;
  return (
    <RegWorkflowContainer id="contact-preference" className="ui-contact-preference">
      <RegWorkflowFactory
        title={props?.fields?.title}
        subTitle={props?.fields?.subTitle}
        currentPage={5}
        totalCount={5}
        buttonsItems={props?.fields?.ctaLinks}
        handleClick={handleClick}
        enableNext={enableNext}
        className="ui-contact-preference__factory"
      >
        <form>
          <Grid container sx={{ marginBottom: '1rem' }}>
            <Controller
              name="contactNumber"
              key={props?.name?.value}
              control={control}
              defaultValue={storedAnswers?.contactNumber}
              rules={{
                required: isPhoneNumberRequired.value ? phoneNumberRequiredErrorMessage?.value : '',
                validate: (value) => {
                  const regEx = RegExp(phoneNumberRegex?.fields?.value?.value);
                  return regEx.test(value) ? true : (phoneNumberRegexErrorMessage?.value ?? '');
                },
              }}
              render={({ field }) => (
                <>
                  <CustomMobileInput
                    label={phoneNumberLabel}
                    control={control}
                    isMobileLabel={IsMobileLabel}
                    isMobileRequired={isMobileRequired}
                    placeholder={phoneNumberPlaceholder?.value}
                    mobileNumberValidationMessageOnSMSConsent={
                      mobileNumberValidationMessageOnSMSConsent
                    }
                    errors={errors}
                    field={field}
                    showIsMobileOption={showIsMobileOption}
                    rules={{
                      validate: (value: boolean) => {
                        if (isSMSConsentVisible() && value === false && formValues.SMS === true) {
                          return mobileNumberValidationMessageOnSMSConsent?.value;
                        }
                        return true;
                      },
                    }}
                  />
                </>
              )}
            />
          </Grid>

          <Grid container>
            <Box sx={{ marginBottom: '1rem' }}>
              <Controller
                name="agree"
                key={props?.name?.value}
                control={control}
                rules={{
                  required: isConsentRequired?.value ? consentRequiredErrorMessage?.value : '',
                  validate: (value, formValues) => {
                    if (value) {
                      setIsConsentsAccepted(value);
                    } else {
                      setIsConsentsAccepted(value);
                    }

                    const atleastOneContactMediumRequired =
                      enableAtleastOneContactMediumMandatedOutOfVisible?.value;
                    const isConsentSelected = consentMediumList.some((consentMedium: any) => {
                      const fieldName = consentMedium?.fields?.key?.value;
                      return formValues[fieldName];
                    });

                    if (atleastOneContactMediumRequired && !isConsentSelected) {
                      return enableAtleastOneContactMediumErrorMessage?.value;
                    }
                    return true;
                  },
                }}
                defaultValue={defaultOptIn?.value}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      control={
                        <>
                          {hideConsentCheckbox?.value ? (
                            <input type="hidden" {...field} />
                          ) : (
                            <Checkbox
                              checked={!!field?.value}
                              sx={{
                                alignSelf: 'flex-start',
                                padding: '0 9px',
                                background: '#ffffff',
                              }}
                              {...field}
                            />
                          )}
                        </>
                      }
                      label={
                        <Box sx={{ paddingLeft: defaultOptIn?.value ? '10px' : 'none' }}>
                          <RichText sx={{ color: '#000000' }} field={consentTitle}></RichText>
                        </Box>
                      }
                      sx={{
                        color: '#444444',
                      }}
                    />
                  </>
                )}
              />
            </Box>

            <Grid
              container
              sx={{
                display:
                  (isConsentsAccepted || defaultOptIn?.value) && !shouldHideTheBox()
                    ? 'block'
                    : 'none',
              }}
            >
              <Box
                sx={{
                  display: 'block',
                  background: '#f2f2f2',
                  marginTop: '0.5rem',
                  padding: '1.4rem',
                  borderRadius: '10px',
                }}
              >
                <Typography
                  sx={{
                    color: '#444444',
                  }}
                >
                  <UiText color="default">
                    <Text field={consentSubTitle}></Text>
                  </UiText>
                </Typography>
                <StConsentMediumFormGroup
                  row={!enableVerticalView}
                  enableVerticalView={enableVerticalView}
                >
                  {consentMediumList &&
                    consentMediumList.map((consentMedium: any, index: any) => {
                      const fieldName = `${consentMedium?.fields?.key?.value}`;
                      const nameVal = String(consentMedium?.fields?.name?.value ?? '').trim();
                      const descVal = String(
                        consentMedium?.fields?.description?.value ?? ''
                      ).trim();
                      const consentMediumLabel =
                        !nameVal && descVal ? (
                          <RichText field={consentMedium?.fields?.description} />
                        ) : (
                          <Text field={consentMedium?.fields?.name} />
                        );
                      if (defaultOptinAll?.value) {
                        return (
                          <Controller
                            key={index}
                            name={fieldName}
                            control={control}
                            defaultValue={true}
                            render={({ field }) => <input type="hidden" {...field} />}
                          />
                        );
                      } else if (!consentMedium?.fields?.hideFromUI?.value) {
                        return (
                          <StConsentMediumGridItem
                            key={index}
                            enableVerticalView={enableVerticalView}
                          >
                            <Controller
                              name={fieldName}
                              key={props?.name?.value}
                              control={control}
                              rules={{
                                validate: (value, values) => {
                                  if (showIsMobileOption?.value) {
                                    if (
                                      values.SMS &&
                                      !!values.isMobile === false &&
                                      isSMSConsentVisible()
                                    ) {
                                      setError('isMobile', {
                                        type: 'manual',
                                        message: mobileNumberValidationMessageOnSMSConsent.value,
                                      });
                                    } else if (
                                      !!values.SMS == false &&
                                      !!values.isMobile === false
                                    ) {
                                      clearErrors('isMobile');
                                    }
                                  }

                                  if (consentMedium?.fields?.isRequired?.value && !value) {
                                    return consentMedium?.fields?.isRequiredErrorMessage?.value;
                                  }
                                  return true;
                                },
                              }}
                              defaultValue={
                                fieldName === 'Email'
                                  ? true
                                  : consentMedium?.fields?.defaultOptIn?.value
                              }
                              render={({ field }) => (
                                <>
                                  <StConsentMediumFormControlLabel
                                    key={index}
                                    enableVerticalView={enableVerticalView}
                                    alignTop={!nameVal && Boolean(descVal)}
                                    primaryFontFamily={
                                      siteSettings?.fontFamily?.fields?.primaryFontFamilyType?.value
                                    }
                                    control={
                                      <StConsentMediumCheckbox
                                        disableRipple
                                        enableVerticalView={enableVerticalView}
                                        defaultChecked={true}
                                        {...field}
                                        checked={!!field?.value}
                                      />
                                    }
                                    label={consentMediumLabel}
                                  />
                                  {/* {JSON.stringify(errors)} */}
                                  {renderError && renderError(fieldName)}
                                </>
                              )}
                            />
                          </StConsentMediumGridItem>
                        );
                      } else if (
                        consentMedium?.fields?.hideFromUI?.value &&
                        consentMedium?.fields?.isRequired?.value
                      ) {
                        return (
                          <Controller
                            key={index}
                            name={fieldName}
                            control={control}
                            defaultValue={true}
                            render={({ field }) => <input type="hidden" {...field} />}
                          />
                        );
                      } else if (
                        consentMedium?.fields?.hideFromUI?.value &&
                        consentMedium?.fields?.defaultOptIn?.value
                      ) {
                        return (
                          <Controller
                            key={index}
                            name={fieldName}
                            control={control}
                            defaultValue={true}
                            render={({ field }) => <input type="hidden" {...field} />}
                          />
                        );
                      } else {
                        return <></>;
                      }
                    })}
                </StConsentMediumFormGroup>
              </Box>
            </Grid>
            {renderError && renderError('agree')}
          </Grid>
        </form>
      </RegWorkflowFactory>
    </RegWorkflowContainer>
  );
};

export default ContactPreference;
